/**
 * Loan repayments & deductions — what a client's liabilities cost them this year, and what part of
 * that cost the tax system gives back, as a pure function.
 *
 * Two outputs, from one walk of the liabilities:
 *   - SCHEDULES: each liability that is actually serviced from personal cashflow, with the frequency
 *     it is serviced at ('weekly' | 'fortnightly' | 'monthly'), its periodic repayment and the annual
 *     total. `calculateFinancials` sums the annual totals into its cashflow; the projection engine's
 *     loanRepaymentPlugin uses the frequency to take each instalment out of the bank when it is really
 *     paid, rather than as one 30 June lump.
 *   - DEDUCTIONS: interest on a mortgage secured against an INVESTMENT property is deductible against
 *     the income of whoever owns that property (split 50/50 when jointly owned). These feed
 *     `calculateFinancials`'s tax calculation, which is why the assessment cannot live server-side.
 *
 * Extracted from `calculateFinancials`'s "--- 5. Loan Repayments & Deductions ---" block, which still
 * calls it — through the injectable {@link LoanRepaymentAssessor} seam, so the projection engine can
 * hand in the assessor owned by its loanRepaymentPlugin. See that plugin for why the code lives here
 * rather than in it.
 *
 * The per-liability annual arithmetic is NOT reimplemented here: it is
 * {@link calculateAnnualLoanRepayment}, which already existed for the trust/company pool
 * (`structureAttribution`) and is now the single definition both paths use.
 */

import { calculateAnnualLoanRepayment, isEntityLiability } from '../../../../../shared/src/calculation/support/structureLiabilitiesUtils.ts';
import { isCapitalisingLoan } from '../../../../../shared/src/calculation/support/capitalisingLoans.ts';
import { shouldSkipPersonalAssetProcessing } from '../../../../../shared/src/calculation/structureAttribution.ts';
import type { FactFind, FactFindLiability } from '../../../../../shared/src/calculation/factFindTypes.ts';

/** The cadences a liability can be serviced at — `FactFindLiability.frequency`, defaulting to monthly. */
export type LoanRepaymentFrequency = 'weekly' | 'fortnightly' | 'monthly';

/**
 * How many times a year each cadence pays. These are the three annualisation factors the engine has
 * always used, and — because a financial year is walked as 365 days — also the number of instalments
 * the projection engine schedules per year. 52 x 7 and 26 x 14 both come to 364, so a weekly or
 * fortnightly stream leaves the year's last day empty rather than doubling up on it.
 */
export const PERIODS_PER_FINANCIAL_YEAR: Record<LoanRepaymentFrequency, number> = {
    weekly: 52,
    fortnightly: 26,
    monthly: 12,
};

/** The order the engine schedules cadences in, and the order a breakdown lists them. */
export const LOAN_REPAYMENT_FREQUENCIES: readonly LoanRepaymentFrequency[] = ['weekly', 'fortnightly', 'monthly'];

/** One liability's servicing schedule for the year. */
export interface LoanRepaymentSchedule {
    /** The liability's id, or undefined for a row that never had one. Carried so a caller can correlate back. */
    id: string | undefined;
    /**
     * Where the liability sits in `factFind.liabilities` — its map key and its index in that array.
     * This is how a caller re-finds the LIVE liability object to pay a period's instalment against and
     * reduce the balance of (see the projection engine's loanRepaymentPlugin). A position rather than
     * the object itself, so this assessment stays a plain serialisable value; and a position rather
     * than the id, because `FactFindLiability.id` is optional.
     */
    category: string;
    index: number;
    /** Display name used in the projection breakdown, e.g. 'Home loan'. */
    description: string;
    /** Category id — 'mortgages', 'personalLoans', … */
    type: string | undefined;
    frequency: LoanRepaymentFrequency;
    /**
     * `'pi'` (principal & interest) repayments pay the debt down; `'io'` (interest-only) ones do not —
     * an interest-only instalment IS the period's interest, so the principal stands still until the
     * loan is refinanced or sold out of. Whoever services this schedule must not amortise an `'io'`
     * balance (see the projection engine's loanRepaymentPlugin).
     */
    loanType: 'pi' | 'io';
    /** The contractual instalment: what the lender takes each period, whatever is still owed. */
    periodicRepayment: number;
    /**
     * The loan's interest rate for ONE period — the annual rate divided by
     * {@link PERIODS_PER_FINANCIAL_YEAR} for its frequency. Each period's interest is this times the
     * balance still outstanding, and only what is left of the instalment after covering it comes off
     * the principal. 0 for a liability with no rate recorded, which then amortises by the whole
     * instalment (there is no basis for splitting it).
     */
    periodicInterestRate: number;
    /** What was outstanding when the year was assessed. */
    openingBalance: number;
    /**
     * What will actually be paid across the year — the sum of the instalments really taken, which is
     * not simply `periodicRepayment x periods`:
     *
     *  - a principal-reducing loan stops costing anything once it is repaid, so a short debt (a credit
     *    card cleared in a few months) pays only what it owed, its final instalment settling the
     *    balance-plus-interest exactly;
     *  - an interest-only loan is never capped, because its payments are a standing cost that never
     *    retires the debt.
     *
     * Keeping this equal to the instalments actually paid is what keeps `calculateFinancials`'s
     * cashflow honest.
     */
    annualRepayment: number;
    /**
     * The interest that will actually accrue across the year — struck period by period on the balance
     * as it reduces, so it is a little less than `openingBalance x rate` for an amortising loan and
     * exactly the whole payment for an interest-only one. This, not the opening-balance figure, is what
     * the tax deduction is based on.
     */
    annualInterest: number;
    /**
     * What will still be owed at the end of the year, after this year's interest and repayments. Equal
     * to {@link openingBalance} for an interest-only loan, and ABOVE it for a loan whose instalment
     * doesn't cover its own interest (the debt is going backwards — worth surfacing, not hiding).
     */
    closingBalance: number;
}

/** One deductible interest amount, and whose income it comes off. */
export interface LoanInterestDeduction {
    owner: 'client' | 'partner';
    name: string;
    amount: number;
}

/** What the liabilities walk found. */
export interface LoanRepaymentAssessment {
    /** Every personally-serviced liability, in liability-map order. */
    schedules: LoanRepaymentSchedule[];
    /** The year's total scheduled repayments across `schedules` — the cashflow cost. */
    totalAnnualRepayments: number;
    /** Investment-property loan interest, already split by owner. Feeds the tax calculation. */
    deductions: LoanInterestDeduction[];
}

/** What the assessment needs. Everything is already-resolved fact-find state. */
export interface LoanRepaymentInput {
    /** Read for `isEntityLiability` (which needs `home`/`assets`) and for the linked-property lookup. */
    factFind: FactFind;
    isPartnered: boolean;
    /**
     * How many years into the projection this assessment is — 0 for the current year (and for any
     * standalone, non-projected call), 1 for the second projected year, and so on. Each year elapsed
     * takes a year off every loan's remaining term; see {@link remainingLoanTermYears}. Optional, and
     * treated as 0 when omitted, so an existing caller keeps assessing the present.
     */
    yearsElapsed?: number;
}

/**
 * Assesses a client's loan repayments and interest deductions. Injected into `calculateFinancials` so
 * a caller can own the rules — see {@link calculateLoanRepayments}, the default, and the projection
 * engine's loanRepaymentPlugin.
 */
export type LoanRepaymentAssessor = (input: LoanRepaymentInput) => LoanRepaymentAssessment;

/**
 * Walks a year of servicing a principal-and-interest loan period by period, to find what is actually
 * paid and what is still owed at the end of it.
 *
 * A repayment is not all principal: each period the balance first accrues its own interest, and only
 * what is left of the instalment after covering that comes off the debt. Amortising by the whole
 * instalment instead — which this engine used to do — has a loan disappear years early, and makes the
 * `loanTerm` a client entered a fiction: a 25-year mortgage would be gone in 18.
 *
 * The last instalment is the payoff figure (balance plus that period's interest), never more, and
 * payments stop the moment nothing is owed. If an instalment cannot even cover the interest the balance
 * GROWS, and this reports that faithfully rather than clamping it.
 */
export function projectPrincipalAndInterestYear(
    openingBalance: number,
    periodicRepayment: number,
    periodicInterestRate: number,
    periods: number,
): { paid: number; interestAccrued: number; closingBalance: number } {
    let balance = openingBalance;
    let paid = 0;
    let interestAccrued = 0;
    for (let period = 0; period < periods && balance > 0; period++) {
        const interest = balance * periodicInterestRate;
        const payment = Math.min(periodicRepayment, balance + interest);
        balance = balance + interest - payment;
        paid += payment;
        interestAccrued += interest;
    }
    return { paid, interestAccrued, closingBalance: balance };
}

/**
 * The years still to run on a loan's term at a given point in a projection.
 *
 * A loan gets one year closer to being repaid every year, and an `auto` repayment has to be derived
 * against what is LEFT of the term, not the whole of it. Deriving it against the whole term every year
 * re-amortises the shrinking balance over a fresh full term, so the instalment shrinks with the debt
 * and the loan decays towards zero without ever arriving — a five-year loan still owing money in year
 * ten. Counting the term down instead reproduces the loan's real schedule: because the balance left
 * after a year of correct amortisation is exactly the balance that same instalment would leave,
 * re-deriving it over the remaining term returns the SAME instalment, and the debt reaches zero exactly
 * when the term expires.
 *
 * Floors at one year so that a residual balance surviving the end of the term is settled rather than
 * frozen (a zero term would mean a zero instalment, and interest would then compound on it forever).
 * Returns the term untouched when there isn't one — an `auto` loan with no term falls back to the
 * entered repayment, and must keep doing so.
 */
export function remainingLoanTermYears(loanTerm: number | undefined, yearsElapsed: number): number | undefined {
    if (!loanTerm || loanTerm <= 0) return loanTerm;
    return Math.max(1, loanTerm - yearsElapsed);
}

/** This liability's servicing cadence. Anything unset or unrecognised is monthly, as it always was. */
export function resolveRepaymentFrequency(liability: FactFindLiability): LoanRepaymentFrequency {
    const frequency = liability.frequency;
    return frequency === 'weekly' || frequency === 'fortnightly' ? frequency : 'monthly';
}

/**
 * True for a liability that is NOT serviced out of personal cashflow, and which therefore has neither
 * a repayment schedule nor a deduction:
 *   - a reverse mortgage or HEAS loan, whose balance is compounded rather than repaid (see
 *     loanPlugin's accrual, and capitalisingLoans.ts for what counts);
 *   - anything the user flagged `excludeFromCashflow`;
 *   - a liability belonging to a trust or company, whose repayments are pooled by `structureAttribution`.
 */
function isOutsidePersonalCashflow(liability: FactFindLiability, factFind: FactFind): boolean {
    if (isEntityLiability(liability, factFind)) return true;
    return isCapitalisingLoan(liability) || liability.excludeFromCashflow === true;
}

/**
 * The interest deduction a liability generates, if any: only a mortgage secured against an INVESTMENT
 * property the client and/or partner personally own. A loan against the family home is private
 * borrowing (`assetLink === 'home'`), and an unlinked loan is not deductible either.
 *
 * `interest` is the interest the loan actually accrues across the year, struck period by period on the
 * reducing balance (see {@link projectPrincipalAndInterestYear}) rather than once on the opening one —
 * so a client claims what the loan really cost them, which for an amortising loan is slightly less.
 */
function deductionsFor(liability: FactFindLiability, factFind: FactFind, isPartnered: boolean, interest: number): LoanInterestDeduction[] {
    if (liability.type !== 'mortgages' || !liability.assetLink || liability.assetLink === 'home') return [];
    // assetLink holds the linked property's (string) id.
    const linkedAsset = factFind.assets?.investmentProperty?.find((p) => p.id === liability.assetLink);
    if (!linkedAsset || shouldSkipPersonalAssetProcessing(linkedAsset.ownership)) return [];

    if (linkedAsset.ownership === 'client') return [{ owner: 'client', name: `Loan Interest (${linkedAsset.description})`, amount: interest }];
    if (linkedAsset.ownership === 'partner') return [{ owner: 'partner', name: `Loan Interest (${linkedAsset.description})`, amount: interest }];
    if (linkedAsset.ownership === 'joint') {
        const name = `Loan Interest (${linkedAsset.description}) (50%)`;
        return [
            { owner: 'client', name, amount: interest / 2 },
            // A "joint" property with no partner on the advice leaves the other half unclaimed.
            ...(isPartnered ? [{ owner: 'partner' as const, name, amount: interest / 2 }] : []),
        ];
    }
    return [];
}

/**
 * Walks a client's liabilities, returning each personally-serviced one's repayment schedule plus the
 * investment-property interest deductions they generate.
 */
export const calculateLoanRepayments: LoanRepaymentAssessor = ({ factFind, isPartnered, yearsElapsed = 0 }: LoanRepaymentInput): LoanRepaymentAssessment => {
    const schedules: LoanRepaymentSchedule[] = [];
    const deductions: LoanInterestDeduction[] = [];
    let totalAnnualRepayments = 0;

    Object.entries(factFind.liabilities || {}).forEach(([category, list]) => {
        (list || []).forEach((liability, index) => {
            if (isOutsidePersonalCashflow(liability, factFind)) return;

            const frequency = resolveRepaymentFrequency(liability);
            const periods = PERIODS_PER_FINANCIAL_YEAR[frequency];
            const loanType = liability.loanType === 'io' ? 'io' : 'pi';
            const openingBalance = liability.balance || 0;
            // The instalment is derived against what is LEFT of the term, so it stays the instalment the
            // client actually pays and the loan finishes when it is supposed to.
            const periodicRepayment =
                calculateAnnualLoanRepayment({ ...liability, loanTerm: remainingLoanTermYears(liability.loanTerm, yearsElapsed) }) / periods;
            const periodicInterestRate = (liability.interestRate || 0) / 100 / periods;
            // An interest-only loan pays its interest and nothing else, so every dollar of its
            // instalments is interest and its balance stands still. A principal-and-interest one is
            // amortised properly — see projectPrincipalAndInterestYear.
            const contractualAnnual = periodicRepayment * periods;
            const { paid: annualRepayment, interestAccrued: annualInterest, closingBalance } =
                loanType === 'io'
                    ? { paid: contractualAnnual, interestAccrued: contractualAnnual, closingBalance: openingBalance }
                    : projectPrincipalAndInterestYear(openingBalance, periodicRepayment, periodicInterestRate, periods);
            totalAnnualRepayments += annualRepayment;
            schedules.push({
                id: liability.id,
                category,
                index,
                description: (liability.description && String(liability.description).trim()) || 'Loan',
                type: liability.type,
                frequency,
                loanType,
                periodicRepayment,
                periodicInterestRate,
                openingBalance,
                annualRepayment,
                annualInterest,
                closingBalance,
            });

            deductions.push(...deductionsFor(liability, factFind, isPartnered, annualInterest));
        });
    });

    return { schedules, totalAnnualRepayments, deductions };
};
